/**
 * Tenant Context Provider
 *
 * Manages tenant state, switching, and onboarding across the application
 */

import type { TenantWithRole } from '@monorepo/shared/schemas/tenants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { TenantSetupDialog } from '@/features/account/components/TenantSetupDialog'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { toast } from '@/lib/toast'
import { tenantService } from '../services/tenantService'

interface TenantContextValue {
  // Current active tenant
  currentTenant: TenantWithRole | null
  // All tenants for the user
  tenants: TenantWithRole[]
  // Loading states
  isLoadingTenants: boolean
  isLoadingCurrentTenant: boolean
  isSwitchingTenant: boolean
  isUpdatingTenant: boolean
  // Actions
  switchTenant: (tenantId: string) => Promise<void>
  setDefaultTenant: (tenantId: string) => Promise<void>
  updateTenant: (data: {
    name?: string
    email?: string
    settings?: Record<string, unknown>
  }) => Promise<void>
  refreshTenants: () => void
  // Utilities
  canManageTenant: (requiredRole?: 'owner' | 'admin') => boolean
  // Error state
  tenantError: Error | null
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined)

interface TenantProviderProps {
  children: ReactNode
}

export function TenantProvider({ children }: TenantProviderProps) {
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useAuthStore()
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(() =>
    tenantService.getCurrentTenantId()
  )
  const [showOnboarding, setShowOnboarding] = useState(false)
  const hasAutoSelected = useRef(false)

  // Fetch all tenants for the user (enabled when authenticated)
  const {
    data: tenantsResponse,
    isLoading: isLoadingTenants,
    error: tenantsError,
    refetch: refreshTenants,
  } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantService.getTenants(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Prevent refetching on mount if data is cached
    refetchOnReconnect: false, // Prevent refetching on reconnect
    refetchOnWindowFocus: false, // Already set globally, but explicit here
    retry: 1, // Only retry once on failure
  })

  const tenants = tenantsResponse?.data || []

  // Check onboarding status when authenticated
  const { data: onboardingStatus } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => tenantService.checkOnboarding(),
    enabled: isAuthenticated && tenants.length > 0,
    staleTime: Infinity, // Only check once per session
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Show onboarding dialog if needed
  useEffect(() => {
    if (onboardingStatus?.needs_onboarding) {
      setShowOnboarding(true)
    }
  }, [onboardingStatus])

  // Fetch current tenant details
  const { data: currentTenant, isLoading: isLoadingCurrentTenant } = useQuery({
    queryKey: ['tenant', currentTenantId],
    queryFn: () => tenantService.getTenant(currentTenantId!),
    enabled: isAuthenticated && !!currentTenantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Switch tenant mutation
  const switchTenantMutation = useMutation({
    mutationFn: (tenantId: string) =>
      tenantService.switchTenant({ tenant_id: tenantId }),
    onSuccess: (_, tenantId) => {
      setCurrentTenantId(tenantId)

      // Clear environment context when switching tenants
      // The new tenant will auto-select its default environment
      localStorage.removeItem('current_environment_id')

      // Invalidate ALL queries except auth, user, and tenants
      // All tables have tenant + environment isolation, so we need to clear everything
      const queriesToInvalidate = [
        'environments',
        'agents',
        'signals',
        'features',
        'plans',
        'customers',
        'contracts',
        'invoices',
        'usage',
        'metrics',
        'events',
        'credits',
        'subscriptions',
      ]

      queriesToInvalidate.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })

      // Refetch current tenant details
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] })

      toast.success('Switched tenant successfully')
    },
    onError: () => {
      toast.error('Failed to switch tenant')
    },
  })

  // Set default tenant mutation
  const setDefaultTenantMutation = useMutation({
    mutationFn: (tenantId: string) =>
      tenantService.setDefaultTenant({ tenant_id: tenantId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast.success('Default tenant updated')
    },
    onError: () => {
      toast.error('Failed to set default tenant')
    },
  })

  // Update tenant mutation
  const updateTenantMutation = useMutation({
    mutationFn: (data: {
      name?: string
      email?: string
      settings?: Record<string, unknown>
    }) => {
      if (!currentTenantId) {
        throw new Error('No active tenant selected')
      }
      return tenantService.updateTenant(currentTenantId, data)
    },
    onSuccess: () => {
      // Invalidate tenant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tenant', currentTenantId] })
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast.success('Tenant updated successfully')
    },
    onError: () => {
      toast.error('Failed to update tenant')
    },
  })

  // Complete onboarding mutation
  const completeOnboardingMutation = useMutation({
    mutationFn: (tenantName: string) =>
      tenantService.completeTenantOnboarding({ tenant_name: tenantName }),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
      queryClient.invalidateQueries({ queryKey: ['tenant', currentTenantId] })
      setShowOnboarding(false)
      toast.success('Workspace setup completed!')
    },
    onError: () => {
      toast.error('Failed to complete workspace setup')
    },
  })

  // Auto-select default tenant or first available tenant if none selected
  useEffect(() => {
    if (!currentTenantId && tenants.length > 0 && !hasAutoSelected.current) {
      // Find default tenant, or use first available tenant as fallback
      const defaultTenant = tenants.find(t => t.is_default) || tenants[0]
      if (defaultTenant) {
        hasAutoSelected.current = true
        setCurrentTenantId(defaultTenant.id)
        localStorage.setItem('current_tenant_id', defaultTenant.id)
      }
    }
  }, [currentTenantId, tenants]) // Use tenants directly with hasAutoSelected guard

  // Switch tenant handler
  const handleSwitchTenant = async (tenantId: string) => {
    await switchTenantMutation.mutateAsync(tenantId)
  }

  // Set default tenant handler
  const handleSetDefaultTenant = async (tenantId: string) => {
    await setDefaultTenantMutation.mutateAsync(tenantId)
  }

  // Update tenant handler
  const handleUpdateTenant = async (data: {
    name?: string
    email?: string
    settings?: Record<string, unknown>
  }) => {
    await updateTenantMutation.mutateAsync(data)
  }

  // Handle onboarding completion
  const handleOnboardingComplete = async (tenantName: string) => {
    await completeOnboardingMutation.mutateAsync(tenantName)
  }

  // Check if user can manage tenant (owner or admin)
  const canManageTenant = (
    requiredRole: 'owner' | 'admin' = 'admin'
  ): boolean => {
    if (!currentTenant) return false

    const roleHierarchy = { owner: 3, admin: 2, member: 1 }
    const userRole = currentTenant.user_role
    const requiredRoleLevel = roleHierarchy[requiredRole]
    const userRoleLevel = roleHierarchy[userRole] || 0

    return userRoleLevel >= requiredRoleLevel
  }

  const value: TenantContextValue = {
    currentTenant: currentTenant || null,
    tenants,
    isLoadingTenants,
    isLoadingCurrentTenant,
    isSwitchingTenant: switchTenantMutation.isPending,
    isUpdatingTenant: updateTenantMutation.isPending,
    switchTenant: handleSwitchTenant,
    setDefaultTenant: handleSetDefaultTenant,
    updateTenant: handleUpdateTenant,
    refreshTenants,
    canManageTenant,
    tenantError: tenantsError || null,
  }

  return (
    <TenantContext.Provider value={value}>
      {children}

      {/* Onboarding Dialog */}
      {showOnboarding && (
        <TenantSetupDialog
          isOpen={showOnboarding}
          onClose={() => {}} // Prevent closing until complete
          onSuccess={handleOnboardingComplete}
          userEmail={user?.email}
        />
      )}
    </TenantContext.Provider>
  )
}

/**
 * Hook to use tenant context
 */
export function useTenant() {
  const context = useContext(TenantContext)

  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }

  return context
}
